import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Check } from "lucide-react";
import api from "../../services/api";

// Sidebar bell — polls /api/inbox/count every 60 s and shows a red badge
// when unread > 0. Click opens a dropdown with recent items; click an
// item to navigate + auto mark-read; "Mark all read" clears the badge.
//
// Designed to be drop-in: parent passes ``collapsed`` so the bell renders
// either as a tile (text + badge) or a compact icon-only button.
export default function NotificationBell({ collapsed = false }) {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  const refreshCount = useCallback(async () => {
    try {
      const res = await api.get("/inbox/count");
      setUnread(res?.data?.unread || 0);
    } catch {}
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const res = await api.get("/inbox", { params: { limit: 25 } });
      setItems(res?.data?.items || []);
    } catch {}
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 60_000);
    return () => clearInterval(id);
  }, [refreshCount]);

  // When the popover opens we always re-fetch the latest list so a user
  // who's been away for a while doesn't see stale entries.
  useEffect(() => {
    if (open) refreshList();
  }, [open, refreshList]);

  // Click outside / Esc closes the popover.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handleItemClick = async (item) => {
    setOpen(false);
    if (!item.read) {
      try { await api.post("/inbox/mark-read", { ids: [item.id] }); }
      catch {}
      setUnread(n => Math.max(0, n - 1));
      setItems(prev => prev.map(it => it.id === item.id ? { ...it, read: true } : it));
    }
    if (item.link) navigate(item.link);
  };

  const handleMarkAllRead = async () => {
    try { await api.post("/inbox/mark-read", { all: true }); }
    catch {}
    setUnread(0);
    setItems(prev => prev.map(it => ({ ...it, read: true })));
  };

  const handleDismiss = async (e, id) => {
    e.stopPropagation();
    try { await api.delete(`/inbox/${id}`); }
    catch {}
    setItems(prev => prev.filter(i => i.id !== id));
    refreshCount();
  };

  return (
    <div className="relative" ref={popRef}>
      {collapsed ? (
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="relative w-full flex items-center justify-center p-2 rounded-lg hover:bg-yellow-500/20 transition-colors"
          aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
          data-testid="notification-bell-collapsed"
        >
          <Bell className={`w-5 h-5 ${unread > 0 ? "text-yellow-400" : "text-white"}`} />
          {unread > 0 && (
            <span
              className="absolute top-0.5 right-1 min-w-[16px] h-[16px] flex items-center justify-center rounded-full text-[9px] font-bold px-1"
              style={{ background: "#EF4444", color: "white" }}
              data-testid="notification-bell-badge"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-yellow-500/20 transition-colors text-left"
          data-testid="notification-bell"
          aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
        >
          <div className="relative">
            <Bell className={`w-5 h-5 ${unread > 0 ? "text-yellow-400" : "text-white/80"}`} strokeWidth={2} />
            {unread > 0 && (
              <span
                className="absolute -top-1 -right-1.5 min-w-[16px] h-[16px] flex items-center justify-center rounded-full text-[9px] font-bold px-1"
                style={{ background: "#EF4444", color: "white" }}
                data-testid="notification-bell-badge"
              >
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold text-white">Notifications</p>
            <p className="text-[10px] text-white/60">
              {unread === 0 ? "All caught up" : `${unread} new`}
            </p>
          </div>
        </button>
      )}

      {/* Popover panel */}
      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 w-[320px] rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
          style={{ borderColor: "#E2E8F0" }}
          data-testid="notification-bell-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "#F1F5F9" }}>
            <p className="text-xs font-black uppercase tracking-wider" style={{ color: "#003366" }}>
              Notifications {unread > 0 && <span className="text-red-500">({unread})</span>}
            </p>
            {unread > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                className="text-[10px] font-bold flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                style={{ color: "#003366" }}
                data-testid="notification-mark-all-read"
              >
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-slate-400" data-testid="notification-empty">
                You have no notifications yet.
              </div>
            ) : items.map(item => (
              <div
                key={item.id}
                role="button"
                tabIndex={0}
                onClick={() => handleItemClick(item)}
                onKeyDown={(e) => { if (e.key === "Enter") handleItemClick(item); }}
                className="px-3 py-2.5 border-b cursor-pointer hover:bg-slate-50 flex items-start gap-2 group"
                style={{ borderColor: "#F1F5F9", background: item.read ? "white" : "rgba(254, 243, 199, 0.4)" }}
                data-testid={`notification-item-${item.id}`}
              >
                {!item.read && (
                  <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-none" style={{ background: "#003366" }} />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-slate-800 truncate">{item.title}</p>
                  {item.body && <p className="text-[11px] text-slate-500 truncate">{item.body}</p>}
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {new Date(item.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDismiss(e, item.id)}
                  className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity"
                  data-testid={`notification-dismiss-${item.id}`}
                  aria-label="Dismiss notification"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <PushOptInRow />
        </div>
      )}
    </div>
  );
}

/* ─────────── Push opt-in nudge ───────────
   Persistent footer inside the bell dropdown. Shows different states:
   - permission "default" → "Enable browser notifications" CTA
   - "granted"            → "On" pill with a small "Disable" link
   - "denied"             → instruction to re-enable in browser settings
   We only render once the user has opened the bell so the bell load
   stays fast on background polls.
*/
function PushOptInRow() {
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const [busy, setBusy] = useState(false);

  if (perm === "unsupported") return null;

  const enable = async () => {
    setBusy(true);
    try {
      const { requestPushOptIn } = await import("../../services/pushNotifications");
      const r = await requestPushOptIn();
      setPerm(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
      if (!r.ok && r.reason === "denied") {
        // Surfaced via the row text — no toast needed.
      }
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const { disablePush } = await import("../../services/pushNotifications");
      await disablePush();
      setPerm(Notification.permission); // stays "granted" at browser level; we just deleted the sub
    } finally {
      setBusy(false);
    }
  };

  if (perm === "granted") {
    return (
      <div className="flex items-center justify-between px-3 py-2 border-t text-[11px]" style={{ borderColor: "#F1F5F9" }}>
        <span className="flex items-center gap-1.5 text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Browser notifications on
        </span>
        <button onClick={disable} disabled={busy} className="font-semibold text-slate-500 hover:text-red-500 disabled:opacity-50">Disable</button>
      </div>
    );
  }
  if (perm === "denied") {
    return (
      <div className="px-3 py-2 border-t text-[11px] text-slate-500" style={{ borderColor: "#F1F5F9" }}>
        Browser notifications blocked. Re-enable in your browser settings.
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between px-3 py-2 border-t text-[11px]" style={{ borderColor: "#F1F5F9" }}>
      <span className="text-slate-500">Get notified when Avida isn't open</span>
      <button
        onClick={enable}
        disabled={busy}
        className="font-semibold px-2 py-1 rounded transition-colors hover:bg-slate-100 disabled:opacity-50"
        style={{ color: "#003366" }}
        data-testid="push-opt-in-btn"
      >
        Enable
      </button>
    </div>
  );
}
